import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus } from "lucide-react";
import { toast } from "sonner";

/**
 * Dropdown backed by an admin-managed lookup collection
 * (e.g. departments, designations). Admins get a "+ New" button next
 * to it that opens a small inline dialog to add & auto-select a new option.
 */
export default function LookupCombo({ kind, value, onChange, placeholder = "Select…", testid = "lookup" }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [options, setOptions] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/lookup/${kind}`).then((r) => setOptions(r.data)).catch(() => {});
  useEffect(() => { load(); }, [kind]);

  const add = async (e) => {
    e?.preventDefault();
    const name = draft.trim();
    if (!name) return;
    setSaving(true);
    try {
      const r = await api.post(`/lookup/${kind}`, { name });
      await load();
      onChange?.(r.data.name);
      setDraft("");
      setDialogOpen(false);
      toast.success(`${name} added`);
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  // If the current value isn't in the fetched list (e.g. legacy free-text),
  // include it as a transient item so the Select can still display it.
  const merged = value && !options.find((o) => o.name === value)
    ? [...options, { id: `_legacy_${value}`, name: value }]
    : options;

  return (
    <div className="flex gap-2">
      <div className="flex-1">
        <Select value={value || undefined} onValueChange={onChange}>
          <SelectTrigger data-testid={`${testid}-trigger`}>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {merged.length === 0 ? (
              <div className="px-2 py-3 text-xs text-muted-foreground text-center">
                No options yet.{isAdmin ? " Click + New." : ""}
              </div>
            ) : (
              merged.map((o) => (
                <SelectItem key={o.id} value={o.name} data-testid={`${testid}-opt-${o.id}`}>{o.name}</SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </div>
      {isAdmin && (
        <Button type="button" size="sm" variant="outline" onClick={() => setDialogOpen(true)} data-testid={`${testid}-add`}>
          <Plus className="w-4 h-4 mr-1" />New
        </Button>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add new {kind === "departments" ? "department" : "designation"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={add} className="space-y-3">
            <Input
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={kind === "departments" ? "e.g. Growth" : "e.g. Senior Backend Engineer"}
              data-testid={`${testid}-input`}
            />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving || !draft.trim()} data-testid={`${testid}-save`}>
                {saving ? "Adding…" : "Add"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
